/**
 * The conversation page's pure model (docs/features/conversations.md §5.13).
 *
 * Everything the page derives without React lives here so it can be tested without a DOM:
 * the 4 intent buckets, a turn's score state, the date band's exact-presence rule, the URL
 * state, which popup scopes exist, and the folds behind the popup.
 *
 * Load-bearing rules:
 *   - A turn's score is `promptScore.score`, written by the agent. It is never recomputed
 *     here, and an absent score is never a 0: it is « — » (unscored, with its reason),
 *     « analyse en cours » (the sub-run is still being analysed) or « non analysé » (the
 *     analysis finished without a prompt verdict — a run older than the feature, or a turn
 *     the time budget skipped).
 *   - A day's presence is shown only where it is EXACT: the conversation summary's 14-day
 *     `spark`. Older days carry per-cell rates only, and a mean of rates is not a rate.
 *   - Citation lists are positional: nothing here dedupes, sorts or filters a turn's
 *     `sources` / `sourceLinks`; the folds only COUNT them.
 */

import {
  CONVERSATION_WORLD_CELL_ZONE,
  launchTs,
  parseCellPath,
  sameEntityName,
  type BrandQueryIntent,
  type ConversationAnalysisStatus,
  type ConversationSubRunExtras,
  type ConversationTurn,
  type ConversationTurnMatch,
  type TargetSourceHighlight,
  type TargetZone,
} from '@traaker/shared';
import { isGroundingWrapper } from '../../../utils/sourceUrl';

// ---------------------------------------------------------------------------
// Intent buckets
// ---------------------------------------------------------------------------

/** The four buckets, keyed like `ConversationIntentId` so the list page's colours apply. */
export type Bucket = 'learn' | 'discover' | 'compare' | 'review';
export const BUCKETS: readonly Bucket[] = ['learn', 'discover', 'compare', 'review'];

/** Same hues as `INTENT_COLORS` in pages/Conversations.tsx. */
export const BUCKET_COLOR: Record<Bucket, string> = {
  learn: '#0f7c8c',
  discover: '#c47a12',
  compare: '#b83280',
  review: '#3d64c8',
};

/** The prompt classifier's intent → the page's bucket. `undefined` = not classified. */
export function bucketOf(intent: BrandQueryIntent | undefined): Bucket | null {
  switch (intent) {
    case 'learn': return 'learn';
    case 'recommend': return 'discover';
    case 'compare': return 'compare';
    case 'describe':
    case 'reputation': return 'review';
    default: return null;
  }
}

// ---------------------------------------------------------------------------
// A turn's score state
// ---------------------------------------------------------------------------

export type TurnScoreView =
  | { kind: 'score'; score: number }
  /** `not_named` = a comparison without the brand; `not_ranked` = named, but the answer ranks nobody. */
  | { kind: 'unscored'; reason: 'not_named' | 'not_ranked' | 'judge_failed' }
  /** The sub-run is still being analysed. */
  | { kind: 'pending' }
  /** Analysis is over and wrote no prompt verdict for this turn. */
  | { kind: 'unanalyzed' };

/**
 * `cellAnalysisStatus` is the RUN# row's `analysisStatus`, which the analysis Lambda writes
 * AFTER every turn verdict; a turn's own `analysisStatus` flips per run, before the prompt
 * verdicts are written, so it cannot tell « not yet » from « never ».
 */
export function turnScoreView(
  turn: Pick<ConversationTurn, 'promptScore' | 'turnStatus'>,
  cellAnalysisStatus: ConversationAnalysisStatus | undefined,
): TurnScoreView {
  const ps = turn.promptScore;
  if (ps) {
    if (typeof ps.score === 'number') return { kind: 'score', score: ps.score };
    if (ps.unscoredReason === 'not_ranked') return { kind: 'unscored', reason: ps.named ? 'not_ranked' : 'not_named' };
    return { kind: 'unscored', reason: ps.unscoredReason ?? 'judge_failed' };
  }
  if (turn.turnStatus === 'failed') return { kind: 'unanalyzed' };
  return cellAnalysisStatus === undefined || cellAnalysisStatus === 'pending'
    ? { kind: 'pending' }
    : { kind: 'unanalyzed' };
}

/** A turn's bucket state, on the same « not yet » / « never » split as its score. */
export function turnBucketView(
  turn: Pick<ConversationTurn, 'promptIntent' | 'turnStatus'>,
  cellAnalysisStatus: ConversationAnalysisStatus | undefined,
): { kind: 'bucket'; bucket: Bucket } | { kind: 'pending' } | { kind: 'unanalyzed' } {
  const bucket = bucketOf(turn.promptIntent);
  if (bucket) return { kind: 'bucket', bucket };
  if (turn.turnStatus === 'failed') return { kind: 'unanalyzed' };
  return cellAnalysisStatus === undefined || cellAnalysisStatus === 'pending'
    ? { kind: 'pending' }
    : { kind: 'unanalyzed' };
}

/** Per bucket: how many turns fell in it, and the mean of the SCORED ones (null if none). */
export function bucketSummary(
  turns: Pick<ConversationTurn, 'promptIntent' | 'promptScore'>[],
): Record<Bucket, { turns: number; mean: number | null }> {
  const acc = Object.fromEntries(BUCKETS.map((b) => [b, { turns: 0, sum: 0, scored: 0 }])) as Record<
    Bucket, { turns: number; sum: number; scored: number }
  >;
  for (const t of turns) {
    const b = bucketOf(t.promptIntent);
    if (!b) continue;
    acc[b].turns += 1;
    if (typeof t.promptScore?.score === 'number') {
      acc[b].sum += t.promptScore.score;
      acc[b].scored += 1;
    }
  }
  return Object.fromEntries(
    BUCKETS.map((b) => [b, { turns: acc[b].turns, mean: acc[b].scored > 0 ? Math.round(acc[b].sum / acc[b].scored) : null }]),
  ) as Record<Bucket, { turns: number; mean: number | null }>;
}

/** BrandAxisPage's `scoreCellColor` bands: ≥ 70 green, ≥ 40 amber, else red. */
/**
 * A day's `PSUM#.intentSpark` entry (turns per prompt intent) folded into the four buckets;
 * null when the day classified nothing. Unknown keys are ignored, never guessed.
 */
export function bucketMix(mix: Record<string, number> | null | undefined): Record<Bucket, number> | null {
  if (!mix) return null;
  const out: Record<Bucket, number> = { learn: 0, discover: 0, compare: 0, review: 0 };
  let total = 0;
  for (const [intent, n] of Object.entries(mix)) {
    const b = bucketOf(intent as BrandQueryIntent);
    if (!b || !(n > 0)) continue;
    out[b] += n;
    total += n;
  }
  return total > 0 ? out : null;
}

export function scoreColor(score: number): { bg: string; fg: string } {
  if (score >= 70) return { bg: '#DCFCE7', fg: '#15803D' };
  if (score >= 40) return { bg: '#FEF3C7', fg: '#92400E' };
  return { bg: '#FEE2E2', fg: '#B91C1C' };
}

// ---------------------------------------------------------------------------
// Date band
// ---------------------------------------------------------------------------

/** UTC day key — the ledger's day is the UTC day of `launchTs(launchId)`. */
export function utcDay(ts: number): string {
  return new Date(ts).toISOString().slice(0, 10);
}

function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * The conversation summary's `spark` (14 presences, newest LAST) keyed by day. The newest
 * index is the UTC day of `computedAt` — the rollup builds it from that day backwards.
 */
export function sparkByDay(summary: { spark?: (number | null)[]; computedAt?: string } | null | undefined): Map<string, number | null> {
  const out = new Map<string, number | null>();
  const spark = summary?.spark;
  const anchor = summary?.computedAt?.slice(0, 10);
  if (!spark || !anchor || !/^\d{4}-\d{2}-\d{2}$/.test(anchor)) return out;
  spark.forEach((v, i) => out.set(addDays(anchor, i - (spark.length - 1)), v));
  return out;
}

export interface BandDay {
  day: string;
  /** The day's launch (the band lists measured days only). */
  launchId: string;
  /** Exact presence, or `undefined` when the day is outside the exact 14-day series. */
  presence: number | null | undefined;
}

/** Days the band shows: the most recent measured ones (owner, 2026-11-02). */
export const BAND_MAX_DAYS = 20;

/**
 * The band's days, oldest first: only days that HAVE a launch, the `BAND_MAX_DAYS` most
 * recent, plus `selected` when it is an older measured day (an old run link must not land
 * on a day the band does not show). An unmeasured day is simply not listed, so nothing
 * can read as 0 %.
 */
export function bandDays(
  launchByDay: Record<string, string>,
  spark: Map<string, number | null>,
  selected: string | null,
): BandDay[] {
  const measured = Object.keys(launchByDay).sort();
  const shown = measured.slice(-BAND_MAX_DAYS);
  if (selected && launchByDay[selected] && !shown.includes(selected)) shown.unshift(selected);
  return shown.map((d) => ({ day: d, launchId: launchByDay[d], presence: spark.has(d) ? spark.get(d) : undefined }));
}

// ---------------------------------------------------------------------------
// URL state
// ---------------------------------------------------------------------------

export interface ViewParams {
  day?: string;
  engine?: string;
  /** `${zone ?? 'world'}~${language}` */
  place?: string;
  run?: number;
}

export function placeKeyOf(zone: string | undefined, language: string): string {
  return `${zone ?? CONVERSATION_WORLD_CELL_ZONE}~${language}`;
}

export function parsePlaceKey(key: string): { zone?: string; language: string } | null {
  const cut = key.lastIndexOf('~');
  if (cut <= 0 || cut === key.length - 1) return null;
  const zone = key.slice(0, cut);
  return { zone: zone === CONVERSATION_WORLD_CELL_ZONE ? undefined : zone, language: key.slice(cut + 1) };
}

export function parseViewParams(sp: URLSearchParams): ViewParams {
  const out: ViewParams = {};
  const day = sp.get('day');
  if (day && /^\d{4}-\d{2}-\d{2}$/.test(day)) out.day = day;
  const engine = sp.get('engine');
  if (engine) out.engine = engine;
  const place = sp.get('place');
  if (place && parsePlaceKey(place)) out.place = place;
  const run = sp.get('run');
  if (run !== null && /^\d+$/.test(run)) out.run = Number(run);
  return out;
}

export function viewParamsToSearch(p: ViewParams): string {
  const sp = new URLSearchParams();
  if (p.day) sp.set('day', p.day);
  if (p.engine) sp.set('engine', p.engine);
  if (p.place) sp.set('place', p.place);
  if (p.run !== undefined) sp.set('run', String(p.run));
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/** The old run URL (…/launches/:launchId/cells/:cellPath/runs/:idx) → this page's params. */
export function runRedirectParams(launchId: string, cellPath: string, idx: string): ViewParams {
  const out: ViewParams = {};
  const ts = launchTs(launchId);
  if (Number.isFinite(ts)) out.day = utcDay(ts);
  const parsed = parseCellPath(cellPath);
  if (parsed) {
    out.engine = parsed.engine;
    out.place = placeKeyOf(parsed.zone, parsed.language);
  }
  if (/^\d+$/.test(idx)) out.run = Number(idx);
  return out;
}

// ---------------------------------------------------------------------------
// Places
// ---------------------------------------------------------------------------

/** « Rue Oberkampf (Paris) » for a street-level zone; the zone's own label otherwise. */
export function placeLabel(zoneMeta: TargetZone | undefined, zone: string | undefined, worldLabel: string): string {
  if (!zoneMeta) return zone ?? worldLabel;
  if (zoneMeta.type === 'address' && zoneMeta.cityName) {
    const head = zoneMeta.label.split(',')[0].trim();
    return head && head !== zoneMeta.cityName ? `${head} (${zoneMeta.cityName})` : zoneMeta.label;
  }
  return zoneMeta.label;
}

// ---------------------------------------------------------------------------
// Popup scopes
// ---------------------------------------------------------------------------

export type ScopeId = 'run' | 'engine' | 'place' | 'day' | 'all';

/**
 * A scope is offered only when it holds more runs than the narrower one it contains; the
 * place needs a second place to exist (with one place it IS the day), and the day must add
 * something to both the engine and the place.
 */
export function availableScopes(c: { engineRuns: number; placeRuns: number; dayRuns: number; places: number }): ScopeId[] {
  const out: ScopeId[] = ['run'];
  if (c.engineRuns > 1) out.push('engine');
  if (c.places > 1 && c.placeRuns > 1) out.push('place');
  if (c.dayRuns > c.engineRuns && c.dayRuns > c.placeRuns) out.push('day');
  out.push('all');
  return out;
}

// ---------------------------------------------------------------------------
// The popup's folds
// ---------------------------------------------------------------------------

export interface FoldedDomain {
  domain: string;
  cit: number;
  own: boolean;
  /** `null` = this scope has no URL detail (the whole-conversation fold). */
  urls: { url: string; cit: number }[] | null;
}
export interface FoldedBrand {
  name: string;
  /** Turns (answers) the brand is named in. */
  turns: number;
  own: boolean;
  /** Mean readable rank; `null` = never ranked, or no rank data in this scope. */
  meanRank: number | null;
}
export interface FoldedTarget {
  targetId: string;
  category: string;
  value: string;
  cit: number;
  viaSource: number;
}
export interface Folded {
  domains: FoldedDomain[];
  brands: FoldedBrand[];
  targets: FoldedTarget[];
  /** Turns counted, when known (a run scope). */
  turns: number | null;
  /** True when a cap dropped entries (or several runs were folded): counts are minimums. */
  floor: boolean;
}

function hostOf(raw: string): string | null {
  try {
    return new URL(raw.startsWith('http') ? raw : `https://${raw}`).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/** The display domain of source `i`: the label first (Gemini/Google carry the site there). */
export function sourceDomainAt(turn: Pick<ConversationTurn, 'sources' | 'sourceLinks'>, i: number): string | null {
  const label = (turn.sources ?? [])[i] ?? '';
  const link = (turn.sourceLinks ?? [])[i] ?? '';
  return (label && !isGroundingWrapper(label) ? hostOf(label) : null) ?? (link && !isGroundingWrapper(link) ? hostOf(link) : null);
}

/**
 * Port of agent-targets `normalize.normalize_url` + `generate_url_segment_prefixes` — the
 * matcher's own URL test, so the page can re-run it on every source. KEEP IN LOCK-STEP
 * with packages/agent-targets/app/targets/normalize.py (vectors in the test file).
 * 'https://www.acme.com/blog/post?utm=x#f' → ['acme.com', 'acme.com/blog', 'acme.com/blog/post',
 * 'acme.com/blog/post?utm=x'].
 */
export function urlSegmentPrefixes(url: string): string[] {
  const norm = url.trim().toLowerCase().replace(/^https?:\/\//, '').split('#')[0].replace(/^www\./, '').replace(/\/+$/, '');
  if (!norm) return [];
  const q = norm.indexOf('?');
  const path = q >= 0 ? norm.slice(0, q) : norm;
  const query = q >= 0 ? norm.slice(q + 1) : '';
  const segments = path.split('/');
  const out = [segments[0]];
  for (let i = 1; i < segments.length; i++) if (segments[i]) out.push(segments.slice(0, i + 1).join('/'));
  if (query) out.push(`${path}?${query}`);
  return out;
}

/** The real URL behind source `i` (never a grounding wrapper), or null. */
function sourceUrlAt(turn: Pick<ConversationTurn, 'sources' | 'sourceLinks'>, i: number): string | null {
  const label = (turn.sources ?? [])[i] ?? '';
  const link = (turn.sourceLinks ?? [])[i] ?? '';
  if (link && !isGroundingWrapper(link)) return link;
  return label && !isGroundingWrapper(label) ? label : null;
}

/**
 * The own URL target behind source `i` — the same rule as prose highlighting: a matched
 * term is re-found EVERYWHERE, not only where the matcher first saw it. First the match
 * recorded at that index; else any own URL match (in a source or in the body) whose value
 * is a segment prefix of this source, i.e. what the matcher would have answered for it.
 * Needed because targets-search keeps ONE source index per signal (« first match wins »,
 * search.py `_probe_url_candidate`): two pierre-fabre.com links, only the first flagged
 * (dev 2026-11-02). A product URL target re-finds its own page, never its retailer's others.
 */
export function ownSourceMatch(
  turn: Pick<ConversationTurn, 'targetMatches' | 'sources' | 'sourceLinks'>,
  i: number,
): ConversationTurnMatch | undefined {
  const own = (turn.targetMatches ?? []).filter((m) => m.kind === 'url');
  // Since 2026-11-02 the search lists every source a url signal matched (`sourceIndexes`);
  // the prefix re-find below covers the turns stored before.
  const at = own.find((m) => m.via === 'source' && (m.sourceIndex === i || m.sourceIndexes?.includes(i)));
  if (at) return at;
  const url = sourceUrlAt(turn, i);
  if (!url) return undefined;
  const prefixes = new Set(urlSegmentPrefixes(url));
  return own.find((m) => prefixes.has(m.value));
}

/** Source `i` is ours (chips, « Citée comme source ×N », the full-answer list). */
export function isOwnSource(turn: Pick<ConversationTurn, 'targetMatches' | 'sources' | 'sourceLinks'>, i: number): boolean {
  return ownSourceMatch(turn, i) !== undefined;
}

/**
 * `highlightsOf(...).sources` completed by `ownSourceMatch`, so every `[n]` marker of an
 * own source is coloured like its chip, in the colour of the target that matched it.
 */
export function ownSourceHighlights(
  turn: Pick<ConversationTurn, 'targetMatches' | 'sources' | 'sourceLinks'>,
  base: TargetSourceHighlight[],
): TargetSourceHighlight[] {
  const out = [...base];
  const have = new Set(base.map((h) => h.sourceIndex));
  const n = Math.max(turn.sources?.length ?? 0, turn.sourceLinks?.length ?? 0);
  for (let i = 0; i < n; i++) {
    if (have.has(i)) continue;
    const m = ownSourceMatch(turn, i);
    if (m) out.push({ sourceIndex: i, targetId: m.targetId, ...(m.rootMarqueId ? { rootMarqueId: m.rootMarqueId } : {}) });
  }
  return out;
}

function mergeBrand(list: FoldedBrand[], name: string, own: boolean): FoldedBrand & { rankSum?: number; ranked?: number } {
  const hit = list.find((b) => b.name === name || sameEntityName(b.name, name));
  if (hit) return hit;
  const b = { name, turns: 0, own, meanRank: null };
  list.push(b);
  return b;
}

/** The run scope: counted from the run's own turns, exactly. */
export function foldTurns(
  turns: ConversationTurn[],
  brand: { name: string; aliases: string[] },
): Folded {
  const domains = new Map<string, FoldedDomain>();
  const targets = new Map<string, FoldedTarget>();
  const brands: (FoldedBrand & { rankSum?: number; ranked?: number })[] = [];
  const isOwn = (n: string) => [brand.name, ...brand.aliases].some((o) => o && sameEntityName(o, n));
  for (const turn of turns) {
    (turn.sourceLinks ?? turn.sources ?? []).forEach((_, i) => {
      const domain = sourceDomainAt(turn, i);
      if (!domain) return;
      let row = domains.get(domain);
      if (!row) { row = { domain, cit: 0, own: false, urls: [] }; domains.set(domain, row); }
      row.cit += 1;
      if (isOwnSource(turn, i)) row.own = true;
      const url = (turn.sourceLinks ?? [])[i] || (turn.sources ?? [])[i] || '';
      if (url && !isGroundingWrapper(url)) {
        const u = row.urls!.find((x) => x.url === url);
        if (u) u.cit += 1; else row.urls!.push({ url, cit: 1 });
      }
    });
    for (const m of turn.targetMatches ?? []) {
      let row = targets.get(m.targetId);
      if (!row) { row = { targetId: m.targetId, category: m.category, value: m.value, cit: 0, viaSource: 0 }; targets.set(m.targetId, row); }
      row.cit += 1;
      if (m.via === 'source') row.viaSource += 1;
    }
    // One count per turn per brand, whichever list named it.
    const named: { name: string; rank: number | null }[] = [];
    const add = (name: string, rank: number | null) => {
      if (!name) return;
      const prev = named.find((n) => n.name === name || sameEntityName(n.name, name));
      if (prev) { if (prev.rank == null && rank != null) prev.rank = rank; return; }
      named.push({ name, rank });
    };
    if (turn.cited) add(brand.name, turn.promptScore?.position ?? turn.citedPosition ?? null);
    for (const [n, r] of Object.entries(turn.lanes ?? {})) add(n, typeof r === 'number' ? r : null);
    (turn.promptScore?.rankedList ?? []).forEach((n, i) => add(n, i + 1));
    for (const n of turn.competitorsMentioned ?? []) add(n, null);
    for (const n of turn.otherBrandsCited ?? []) add(n, null);
    for (const { name, rank } of named) {
      const own = isOwn(name);
      const b = mergeBrand(brands, own ? brand.name : name, own);
      b.turns += 1;
      if (rank != null) { b.rankSum = (b.rankSum ?? 0) + rank; b.ranked = (b.ranked ?? 0) + 1; }
    }
  }
  return {
    domains: [...domains.values()].sort((a, b) => b.cit - a.cit),
    targets: [...targets.values()].sort((a, b) => b.cit - a.cit),
    brands: brands
      .map(({ rankSum, ranked, ...b }) => ({ ...b, meanRank: ranked ? rankSum! / ranked : null }))
      .sort((a, b) => b.turns - a.turns),
    turns: turns.length,
    floor: false,
  };
}

type ExtrasLike = Pick<ConversationSubRunExtras, 'topDomains' | 'topTargets' | 'mentionCounts' | 'residualDomains' | 'residualTargets'>
  & Partial<Pick<ConversationSubRunExtras, 'topUrls' | 'rankStats'>>;

/**
 * Several cells' RUNX# rows (or the `/extras` window fold) summed. Each row keeps its own
 * top 20 domains / 30 cibles, so beyond one row every count is a MINIMUM (`floor`).
 * `withUrls` / ranks only exist on RUNX#: the window fold has neither.
 */
export function foldExtras(
  rows: ExtrasLike[],
  brand: { name: string; aliases: string[] },
  opts: { floor?: boolean } = {},
): Folded {
  const domains = new Map<string, FoldedDomain>();
  const targets = new Map<string, FoldedTarget>();
  const brands: (FoldedBrand & { rankSum?: number; ranked?: number })[] = [];
  const withUrls = rows.length > 0 && rows.every((r) => Array.isArray(r.topUrls));
  const isOwn = (n: string) => [brand.name, ...brand.aliases].some((o) => o && sameEntityName(o, n));
  let floor = !!opts.floor || rows.length > 1;
  for (const r of rows) {
    for (const d of r.topDomains ?? []) {
      let row = domains.get(d.domain);
      if (!row) { row = { domain: d.domain, cit: 0, own: false, urls: withUrls ? [] : null }; domains.set(d.domain, row); }
      row.cit += d.cit;
      if (d.ownDomain) row.own = true;
    }
    if (withUrls) {
      for (const u of r.topUrls ?? []) {
        const row = domains.get(u.domain);
        if (!row || !row.urls || isGroundingWrapper(u.url)) continue;
        const hit = row.urls.find((x) => x.url === u.url);
        if (hit) hit.cit += u.cit; else row.urls.push({ url: u.url, cit: u.cit });
      }
    }
    for (const t of r.topTargets ?? []) {
      let row = targets.get(t.targetId);
      if (!row) { row = { targetId: t.targetId, category: t.category, value: t.value, cit: 0, viaSource: 0 }; targets.set(t.targetId, row); }
      row.cit += t.cit;
    }
    for (const [name, turns] of Object.entries(r.mentionCounts ?? {})) {
      const own = isOwn(name);
      const b = mergeBrand(brands, own ? brand.name : name, own);
      b.turns += turns;
      const rs = r.rankStats?.[name];
      if (rs && rs.ranked > 0) { b.rankSum = (b.rankSum ?? 0) + rs.sumRank; b.ranked = (b.ranked ?? 0) + rs.ranked; }
    }
    if ((r.residualDomains?.count ?? 0) > 0 || (r.residualTargets?.count ?? 0) > 0) floor = true;
  }
  for (const d of domains.values()) d.urls?.sort((a, b) => b.cit - a.cit);
  return {
    domains: [...domains.values()].sort((a, b) => b.cit - a.cit),
    targets: [...targets.values()].sort((a, b) => b.cit - a.cit),
    brands: brands
      .map(({ rankSum, ranked, ...b }) => ({ ...b, meanRank: ranked ? rankSum! / ranked : null }))
      .sort((a, b) => b.turns - a.turns),
    turns: null,
    floor,
  };
}

// ---------------------------------------------------------------------------
// Matrix rows
// ---------------------------------------------------------------------------

export const TERMINAL_STATUSES = new Set(['completed', 'partial', 'failed']);

/** A terminal cell is readable only once the finalizer wrote `stats` + the `runs[]` array. */
export function isFinalized<T extends { status: string; stats?: unknown; runs?: unknown }>(cell: T | null | undefined): cell is T {
  return !!cell && TERMINAL_STATUSES.has(cell.status)
    && (cell.status === 'failed' || (cell.stats != null && Array.isArray(cell.runs)));
}

// ---------------------------------------------------------------------------
// Turn helpers
// ---------------------------------------------------------------------------

/** First turn the brand is absent from after having been cited. */
export function dropOutTurnOf(turns: Pick<ConversationTurn, 'cited' | 'turn'>[]): number | null {
  let seen = false;
  for (const turn of turns) {
    if (turn.cited) { seen = true; continue; }
    if (seen) return turn.turn;
  }
  return null;
}

/** Competitor names in a turn, the brand excluded, first spelling kept. */
export function competitorsInTurn(turn: ConversationTurn, brand: { name: string; aliases: string[] }): string[] {
  const own = (n: string) => [brand.name, ...brand.aliases].some((o) => o && sameEntityName(o, n));
  const out: string[] = [];
  for (const n of [...(turn.competitorsMentioned ?? []), ...(turn.promptScore?.rankedList ?? [])]) {
    if (!n || own(n) || out.some((x) => x === n || sameEntityName(x, n))) continue;
    out.push(n);
  }
  return out;
}
